import {
  Group,
  List,
  LoadingOverlay,
  Stack,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core"
import { IconCircleCheck, IconCircleX } from "@tabler/icons-react"
import { Card } from "@tfg/ui"
import { useTranslation } from "react-i18next"

import { CustomDrawer } from "~/components/CustomDrawer.tsx"
import {
  ComplianceDetailsDto,
  FindProductsProductStandardDto,
  useFindProductStandardCertificates,
} from "~/gen/api"
import { CertificateSummary } from "~/pages/Brand/Certificates/components/CertificateSummary.tsx"
import { standardRulesTranslations } from "~/pages/Brand/Certificates/type.ts"

type Props = {
  standard: FindProductsProductStandardDto
  productId: string
  onClose: () => void
}

export const StandardComplianceDrawer = ({
  standard,
  productId,
  onClose,
}: Props) => {
  return (
    <CustomDrawer size="lg" onClose={onClose} opened={true}>
      <Group
        mih="100%"
        bg="var(--mantine-color-brand-background)"
        align="start"
      >
        <Stack w="100%" p="xl" justify="start" align="start">
          <Title order={2}>{standard.name}</Title>
          <ComplianceStandardView standard={standard} productId={productId} />
        </Stack>
      </Group>
    </CustomDrawer>
  )
}

const CompliantIcon = () => (
  <ThemeIcon color="primary" size={24} radius="xl">
    <IconCircleCheck style={{ width: "16px", height: "16px" }} />
  </ThemeIcon>
)

const NotCompliantIcon = () => (
  <ThemeIcon color="#E0525B" size={24} radius="xl">
    <IconCircleX style={{ width: "16px", height: "16px" }} />
  </ThemeIcon>
)

const ComplianceStandardView = ({
  standard,
  productId,
}: {
  standard: FindProductsProductStandardDto
  productId: string
}) => {
  const { t } = useTranslation("certificates")

  const { data: certificatesData, isLoading: isLoadingCertificates } =
    useFindProductStandardCertificates({
      productId,
      standardId: standard.id,
    })

  if (!certificatesData || isLoadingCertificates) {
    return <LoadingOverlay visible={true} overlayProps={{ blur: 2 }} />
  }

  const hasMissingCertificates =
    certificatesData.data.processed < certificatesData.data.total ||
    certificatesData.data.processed < 2

  return (
    <>
      <Title order={6}>
        {t("standard.drawer.certificatesNumber", {
          completedCertificates: certificatesData.data.processed,
          totalCertificates: certificatesData.data.total,
        })}
      </Title>
      {certificatesData.data.certificates.map((certificate, index) => (
        <Stack
          bg="var(--mantine-color-brand-background)"
          h="100%"
          p="md"
          justify="start"
          align="start"
          key={index}
        >
          <Card style={{ width: "100%" }}>
            <Stack>
              <CertificateSummary
                showDocumentIcons={true}
                certificate={certificate}
                maxListElements={5}
              />
            </Stack>
          </Card>
        </Stack>
      ))}
      {!hasMissingCertificates ? (
        <>
          <Title order={4}>{t("standard.drawer.complianceRules")}</Title>
          <List ml="xl" spacing="lg" size="md" center>
            {standard.complianceDetails.details.map(
              (rule: ComplianceDetailsDto, index) => {
                const icon = rule.isCompliant ? (
                  <CompliantIcon />
                ) : (
                  <NotCompliantIcon />
                )

                return (
                  <Tooltip
                    key={index}
                    color="white"
                    c="black"
                    position="top-start"
                    offset={5}
                    label={t(standardRulesTranslations[rule.description])}
                    multiline
                    maw={400}
                  >
                    <List.Item icon={icon}>
                      {t(standardRulesTranslations[rule.name])}
                    </List.Item>
                  </Tooltip>
                )
              }
            )}
          </List>
        </>
      ) : null}
    </>
  )
}
